import type { SearchResponse } from '@algolia/client-search';

import { Icon } from '#/components/Icon/Icon';
import { PostListingLink } from '#/components/Link/PostListingLink';
import { SortMenu } from '#/components/SortMenu/SortMenu';
import { Text } from '#/components/Text/Text';
import { useViewportBreakpointValue } from '#/hooks/viewport/useViewportBreakpointValue';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchAlgoliaHit } from '#/modules/search/algolia/types/SearchAlgoliaHit';
import { SaveSearchButton } from '#/modules/search/components/SaveSearch/SaveSearchButton';
import type { SearchSortType } from '#/modules/search/types/SearchSortType';
import type { SearchType } from '#/modules/search/types/SearchType';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import {
  SearchResultsHeaderHolder,
  SearchResultsHeaderLeftSide,
  SearchResultsHeaderLeftSideSort,
  SearchResultsHeaderPostAListingHolder,
  SearchResultsHeaderRightSide,
} from './SearchResultsHeader.styled';
import { SearchResultsHeaderCount } from './SearchResultsHeaderCount';

type Props = {
  results: SearchResponse<SearchAlgoliaHit> | undefined;
  showSortOptions: boolean;
  searchType: SearchType;
  sort: SearchSortType;
  saveSearch: () => Promise<void>;
  onChangeSort: (sort: SearchSortType) => void;
};

export function SearchResultsHeader({
  results,
  showSortOptions,
  searchType,
  sort,
  saveSearch,
  onChangeSort,
}: Props) {
  const { i18n } = useI18n();
  const showShortText = useViewportBreakpointValue({ xs: true, sm: false });

  return (
    <SearchResultsHeaderHolder>
      <SearchResultsHeaderLeftSide>
        <SearchResultsHeaderCount
          count={results ? results.nbHits! : null}
          searchType={searchType}
          shortText={showShortText}
        />

        {showSortOptions && (
          <SearchResultsHeaderLeftSideSort>
            <SortMenu
              value={sort}
              options={[
                { name: i18n('Best Match'), param: 'relevance' },
                { name: i18n('Newest'), param: 'newest' },
              ]}
              onChange={onChangeSort}
            />
          </SearchResultsHeaderLeftSideSort>
        )}
      </SearchResultsHeaderLeftSide>

      {
        // Wait for the search to happen to avoid these buttons being unresponsive due to HIGH CPU usage
        results && (
          <SearchResultsHeaderRightSide>
            {searchType === 'JOB' && (
              <SearchResultsHeaderPostAListingHolder>
                <PostListingLink
                  onClick={() => {
                    trackClicked('Post A Listing', {
                      context: 'Main Search Results',
                    });
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ marginTop: '1px', display: 'inline-flex' }}>
                      <Icon name="pin" size={20} />
                    </div>
                    <Text
                      style={{
                        marginLeft: '0.6em',
                        marginRight: '0.3em',
                        fontSize: '16px',
                      }}
                    >
                      {/* TRANSLATORS: Post a Job in search results */}
                      {i18n('Post a Job')}
                    </Text>
                  </div>
                </PostListingLink>
              </SearchResultsHeaderPostAListingHolder>
            )}

            <SaveSearchButton saveSearch={saveSearch} />
          </SearchResultsHeaderRightSide>
        )
      }
    </SearchResultsHeaderHolder>
  );
}
